import addressFormatter, { CommonOptions } from "@fragaria/address-formatter";
import { rejectFalsy } from "@sinch/utils/array";
import { values } from "ramda";
import { isNotNilOrEmpty } from "ramda-adjunct";
import { IntlShape, useIntl } from "react-intl";
import { ExpandedFormats } from "@sinch/core/hooks/format.types";

interface AddressParts {
  address?: string | null;
  city?: string | null;
  country?: string | null;
  name?: string | null;
  region?: string | null;
  zip?: string | null;
}

export const useFormatAddress = () => {
  const addressFormat = addressFormatter.format;
  const { formats, formatDisplayName } = useIntl() as IntlShape & {
    formats: ExpandedFormats;
  };
  return (value: AddressParts, { format }: { format?: keyof ExpandedFormats["address"] }) => {
    const formatVariant: CommonOptions = format ? formats.address[format] : {};

    const addressParts = rejectFalsy({
      attention: value.name,
      street: value.address,
      city: value.city,
      region: value.region,
      postcode: value.zip,
    });
    const placePartsArray = [
      ...(addressFormat(addressParts, {
        ...formatVariant,
        appendCountry: false,
        countryCode: value.country && formatVariant.abbreviate ? value.country : undefined,
        output: "array",
      }) ?? values(addressParts)),
      // In almost all countries, country is last, expand functionality to show abbrevied country
      ...(value.country && formatVariant.appendCountry
        ? [
            value.country.match(/^\w{2}|\d{3}$/)?.[0] === value.country
              ? formatDisplayName(value.country ?? "", { type: "region" })
              : value.country,
          ]
        : []),
    ];

    return placePartsArray.filter(isNotNilOrEmpty).join(", ");
  };
};
